import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { z } from 'zod';
import fs from 'fs';
import path from 'path';
import { compileCollections } from '@stackwright-pro/pulse/server';
import { compileAuth } from '@stackwright-pro/auth-nextjs/server';
import { compileIntegrations } from '@stackwright-pro/openapi/server';

// ---------------------------------------------------------------------------
// Context builder — mirrors CompileContext from @stackwright/build-scripts
// Used instead of importing build-scripts directly (not yet published with
// the compile-primitive split from Bead 1 / swp-xyia).
// TODO(swp-rh6j): replace with createCompileContext() once
// @stackwright/build-scripts ^0.10.x is published with compile primitives.
// ---------------------------------------------------------------------------

interface MinimalPluginContext {
  projectRoot: string;
  contentOutDir: string;
  imagesDir: string;
  siteConfig: Record<string, unknown>;
}

function buildContext(projectRoot: string): MinimalPluginContext {
  return {
    projectRoot,
    contentOutDir: path.join(projectRoot, 'public', 'stackwright-content'),
    imagesDir: path.join(projectRoot, 'public', 'images'),
    siteConfig: loadSiteConfig(projectRoot),
  };
}

function loadSiteConfig(projectRoot: string): Record<string, unknown> {
  try {
    const sitePath = path.join(projectRoot, 'public', 'stackwright-content', '_site.json');
    return JSON.parse(fs.readFileSync(sitePath, 'utf8')) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function formatDuration(startMs: number): string {
  const ms = Date.now() - startMs;
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(2)}s`;
}

// ---------------------------------------------------------------------------
// OSS compile shim — dynamic import so the tools degrade gracefully if
// @stackwright/build-scripts isn't installed in the calling project.
// ---------------------------------------------------------------------------

async function tryOssCompile(
  fn: string,
  ctx: MinimalPluginContext,
  extra?: Record<string, unknown>
): Promise<{ ok: boolean; error?: string }> {
  try {
    // Resolve from the project's own node_modules (not the MCP package's)
    const bsPath = path.join(ctx.projectRoot, 'node_modules', '@stackwright', 'build-scripts');
    if (!fs.existsSync(bsPath)) {
      return {
        ok: false,
        error: `@stackwright/build-scripts not found at ${bsPath}. Is it installed in your project?`,
      };
    }
    const bs = (await import(bsPath)) as Record<
      string,
      (ctx: unknown, ...args: unknown[]) => Promise<void>
    >;
    if (typeof bs[fn] !== 'function') {
      return { ok: false, error: `${fn} not exported from @stackwright/build-scripts` };
    }
    await bs[fn](ctx, ...(extra ? [extra] : []));
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : String(err) };
  }
}

// ---------------------------------------------------------------------------
// Tool registration
// ---------------------------------------------------------------------------

/**
 * Register Pro compile MCP tools (stackwright_pro_compile_* namespace).
 */
export function registerCompileTools(server: McpServer): void {
  const projectRootArg = z
    .string()
    .optional()
    .describe('Absolute path to project root (defaults to cwd)');

  // ── stackwright_pro_compile_collections ────────────────────────────────────
  server.tool(
    'stackwright_pro_compile_collections',
    'Compile stackwright.collections.yml → public/stackwright-content/_collections.json. ' +
      'Reads and validates the live-collections sidecar config file and emits the JSON sink ' +
      'consumed at runtime by PulseCollectionProvider.',
    { projectRoot: projectRootArg },
    async ({ projectRoot }) => {
      const start = Date.now();
      const root = projectRoot ?? process.cwd();
      try {
        await compileCollections(buildContext(root));
        return {
          content: [
            {
              type: 'text' as const,
              text: ` _collections.json written (${formatDuration(start)})`,
            },
          ],
        };
      } catch (err) {
        return {
          content: [
            {
              type: 'text' as const,
              text: ` compile_collections failed: ${err instanceof Error ? err.message : String(err)}`,
            },
          ],
        };
      }
    }
  );

  // ── stackwright_pro_compile_auth ───────────────────────────────────────────
  server.tool(
    'stackwright_pro_compile_auth',
    'Compile stackwright.auth.yml → public/stackwright-content/_auth.json. ' +
      'Reads and validates the auth sidecar config file and emits the JSON sink ' +
      'consumed at app startup to configure auth middleware.',
    { projectRoot: projectRootArg },
    async ({ projectRoot }) => {
      const start = Date.now();
      const root = projectRoot ?? process.cwd();
      try {
        await compileAuth(buildContext(root));
        return {
          content: [
            {
              type: 'text' as const,
              text: ` _auth.json written (${formatDuration(start)})`,
            },
          ],
        };
      } catch (err) {
        return {
          content: [
            {
              type: 'text' as const,
              text: ` compile_auth failed: ${err instanceof Error ? err.message : String(err)}`,
            },
          ],
        };
      }
    }
  );

  // ── stackwright_pro_compile_integrations ───────────────────────────────────
  server.tool(
    'stackwright_pro_compile_integrations',
    'Compile stackwright.integrations.yml → public/stackwright-content/_integrations.json. ' +
      'If no yml file exists, falls back to extracting integration metadata from _site.json ' +
      '(collections data is excluded — it lives in _collections.json).',
    { projectRoot: projectRootArg },
    async ({ projectRoot }) => {
      const start = Date.now();
      const root = projectRoot ?? process.cwd();
      try {
        await compileIntegrations(buildContext(root));
        return {
          content: [
            {
              type: 'text' as const,
              text: ` _integrations.json written (${formatDuration(start)})`,
            },
          ],
        };
      } catch (err) {
        return {
          content: [
            {
              type: 'text' as const,
              text: ` compile_integrations failed: ${err instanceof Error ? err.message : String(err)}`,
            },
          ],
        };
      }
    }
  );

  // ── stackwright_pro_compile_all ────────────────────────────────────────────
  server.tool(
    'stackwright_pro_compile_all',
    'Run all Pro compile sinks (collections + auth + integrations) in sequence. ' +
      'Equivalent to running compile_collections, compile_auth, and compile_integrations ' +
      'individually. Use this for a full Pro prebuild pass.',
    { projectRoot: projectRootArg },
    async ({ projectRoot }) => {
      const start = Date.now();
      const root = projectRoot ?? process.cwd();
      const ctx = buildContext(root);
      const results: string[] = [];
      const errors: string[] = [];

      for (const [label, fn] of [
        ['collections', () => compileCollections(ctx)],
        ['auth', () => compileAuth(ctx)],
        ['integrations', () => compileIntegrations(ctx)],
      ] as const) {
        try {
          await fn();
          results.push(`   _${label}.json`);
        } catch (err) {
          errors.push(`   ${label}: ${err instanceof Error ? err.message : String(err)}`);
        }
      }

      const summary =
        errors.length > 0
          ? ` compile_all finished with errors (${formatDuration(start)}):\n${results.join('\n')}\nErrors:\n${errors.join('\n')}`
          : ` compile_all complete (${formatDuration(start)}):\n${results.join('\n')}`;

      return { content: [{ type: 'text' as const, text: summary }] };
    }
  );

  // ── stackwright_pro_compile_theme ──────────────────────────────────────────
  server.tool(
    'stackwright_pro_compile_theme',
    'Compile stackwright.yml theme config → _theme.json. Delegates to compileTheme from ' +
      '@stackwright/build-scripts installed in the project.',
    { projectRoot: projectRootArg },
    async ({ projectRoot }) => {
      const start = Date.now();
      const root = projectRoot ?? process.cwd();
      const { ok, error } = await tryOssCompile('compileTheme', buildContext(root));
      return {
        content: [
          {
            type: 'text' as const,
            text: ok
              ? ` _theme.json written (${formatDuration(start)})`
              : ` compile_theme failed: ${error}`,
          },
        ],
      };
    }
  );

  // ── stackwright_pro_compile_site ───────────────────────────────────────────
  server.tool(
    'stackwright_pro_compile_site',
    'Compile stackwright.yml → _site.json. Delegates to compileSite from ' +
      '@stackwright/build-scripts installed in the project.',
    { projectRoot: projectRootArg },
    async ({ projectRoot }) => {
      const start = Date.now();
      const root = projectRoot ?? process.cwd();
      const { ok, error } = await tryOssCompile('compileSite', buildContext(root));
      return {
        content: [
          {
            type: 'text' as const,
            text: ok
              ? ` _site.json written (${formatDuration(start)})`
              : ` compile_site failed: ${error}`,
          },
        ],
      };
    }
  );

  // ── stackwright_pro_compile_pages ──────────────────────────────────────────
  server.tool(
    'stackwright_pro_compile_pages',
    'Compile all pages content.yml files → page JSON files. Delegates to compilePages from ' +
      '@stackwright/build-scripts installed in the project.',
    { projectRoot: projectRootArg },
    async ({ projectRoot }) => {
      const start = Date.now();
      const root = projectRoot ?? process.cwd();
      const { ok, error } = await tryOssCompile('compilePages', buildContext(root));
      return {
        content: [
          {
            type: 'text' as const,
            text: ok
              ? ` pages compiled (${formatDuration(start)})`
              : ` compile_pages failed: ${error}`,
          },
        ],
      };
    }
  );

  // ── stackwright_pro_compile_page ───────────────────────────────────────────
  server.tool(
    'stackwright_pro_compile_page',
    'Compile a single page by slug. Delegates to compilePage from ' +
      '@stackwright/build-scripts installed in the project.',
    {
      slug: z.string().describe('Page slug (relative path under pages/, e.g. "dashboard")'),
      projectRoot: projectRootArg,
    },
    async ({ slug, projectRoot }) => {
      const start = Date.now();
      const root = projectRoot ?? process.cwd();
      const { ok, error } = await tryOssCompile('compilePage', buildContext(root), { slug });
      return {
        content: [
          {
            type: 'text' as const,
            text: ok
              ? ` page "${slug}" compiled (${formatDuration(start)})`
              : ` compile_page failed: ${error}`,
          },
        ],
      };
    }
  );
}
